import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, MousePointerClick, QrCode, Contact, Share2 } from "lucide-react";

const PERIODS = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["90d", "90 jours"], ["365d", "1 an"]];
const KEY_LABELS = { call: "Appels", email: "E-mails", whatsapp: "WhatsApp", website: "Site web", directions: "Itinéraire", devis: "Devis", sms: "SMS" };

export default function StatsCarte() {
  const { id } = useParams();
  const nav = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/cartes/${id}/stats?period=${period}`); setData(data); }
    catch { toast.error("Statistiques indisponibles."); nav("/ma-carte"); }
  }, [id, period, nav]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const cards = [
    ["view", "Vues", totals.view || 0, Eye, "#3B82F6"],
    ["click", "Clics boutons", totals.click || 0, MousePointerClick, "#E05638"],
    ["qr_scan", "Scans QR", totals.qr_scan || 0, QrCode, "#7C3AED"],
    ["vcard", "Contacts (vCard)", totals.vcard || 0, Contact, "#059669"],
    ["share", "Partages", totals.share || 0, Share2, "#DB2777"],
  ];
  const maxView = Math.max(1, ...(data?.series || []).map((s) => s.views));

  return (
    <div data-testid="stats-carte-page">
      <Button variant="ghost" size="sm" onClick={() => nav("/ma-carte")} className="mb-3 text-slate-500"><ArrowLeft size={16} className="mr-1" /> Mes cartes</Button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="font-heading text-2xl font-bold">Statistiques</h1>
        <div className="flex gap-1.5 flex-wrap">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${period === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {cards.map(([key, label, val, Icon, color]) => (
          <div key={key} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`stat-${key}`}>
            <Icon size={20} style={{ color }} />
            <div className="text-2xl font-extrabold mt-2 text-slate-900">{val}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <h2 className="font-bold text-slate-800 mb-3 text-sm">Vues par jour</h2>
          {(data?.series || []).length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">Aucune donnée sur la période.</p> : (
            <div className="flex items-end gap-1 h-40">
              {data.series.map((s) => (
                <div key={s.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${s.date}: ${s.views}`}>
                  <div className="w-full rounded-t bg-[#3B82F6]" style={{ height: `${(s.views / maxView) * 100}%`, minHeight: 2 }} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <h2 className="font-bold text-slate-800 mb-3 text-sm">Clics par bouton</h2>
          {(data?.clicks || []).length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">Aucun clic sur la période.</p> : (
            <div className="space-y-2">
              {data.clicks.map((c) => (
                <div key={c.key} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{KEY_LABELS[c.key] || c.key}</span>
                  <span className="font-bold text-slate-900">{c.n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-4">Les données sont réellement mesurées à partir des consultations publiques de votre carte. Les scans de QR sont comptabilisés lorsque le lien est ouvert depuis le QR.</p>
    </div>
  );
}
